// Feed triage: grouping, severity, and filtering for the report feed (pitch 5, 2026-09-21 pitches, "Feed
// triage" - docs/planning/2026-09-21-pitches.md). Pure and event-log-driven, like `src/ui/reports.ts`
// next to it: no store subscriptions, no React, so it is trivial to test and trivial to reuse from a
// debrief view later.
//
// Problem this answers (independent review, 2026-09-21, finding 5): 135 cards in two sim hours, six
// identical "pinned" cards at one timestamp, no way to see only what matters. The fix is not a smarter
// feed - it is a duller one that groups routine, same-kind traffic and ranks the rest by how much a
// planner needs to see it right now.

import type { EventKind } from '../../sim/types';
import { TICKS_PER_MIN } from '../../sim/types';
import type { Report } from '../reports';

export type Severity = 'critical' | 'high' | 'normal' | 'low';

/** Lower sorts first: a critical event outranks a routine one within the same sim minute. */
const SEVERITY_RANK: Record<Severity, number> = { critical: 0, high: 1, normal: 2, low: 3 };

const CRITICAL_KINDS = new Set<EventKind>(['unit.destroyed', 'unit.broken', 'culminated', 'obj.seized']);
// `order.rejected` is high, not low, and sits with them: an order the player gave that the force will
// not carry out is the one piece of order traffic they have to read. It used to rank below an
// acknowledgement, which buried "No observer on target" under the cap on visible rows (pitch 9c).
const HIGH_KINDS = new Set<EventKind>(['nai.contact', 'force.fuelLow', 'net.saturated', 'order.rejected']);
const NORMAL_KINDS = new Set<EventKind>(['sitrep', 'pl.crossed', 'fire.mission', 'unit.pinned']);
const LOW_KINDS = new Set<EventKind>([
  'order.issued', 'order.tx', 'order.received', 'order.exec', 'order.done', 'order.cancelled',
  'contact.lost', 'unit.unpinned',
]);

/**
 * The severity table from the pitch. `contact.new` splits on the event's own `significant` flag (a
 * confirmed report inside a named area of interest outranks routine contact chatter); every other kind
 * not named in the table (unit.fuelLow, unit.ammoLow, uas.lost, phase, red.decision never reaches the
 * feed) reads as ordinary staff traffic rather than silently critical or silently ignorable.
 */
export function severityOf(item: { kind: EventKind; significant: boolean }): Severity {
  if (CRITICAL_KINDS.has(item.kind)) return 'critical';
  if (item.kind === 'contact.new') return item.significant ? 'high' : 'normal';
  if (HIGH_KINDS.has(item.kind)) return 'high';
  if (NORMAL_KINDS.has(item.kind)) return 'normal';
  if (LOW_KINDS.has(item.kind)) return 'low';
  return 'normal';
}

/** Left-border colour for a severity, reusing the palette already in `src/styles.css` rather than
 * inventing a new one: red for critical, amber (the existing "significant" amber) for high, fog for
 * normal, and the same slate the card border already uses by default for low ("none"). */
export function severityColor(sev: Severity): string {
  switch (sev) {
    case 'critical': return 'var(--color-hostile)';
    case 'high': return 'var(--color-warn)';
    case 'normal': return 'var(--color-fog)';
    case 'low': return 'var(--color-slate-700)';
  }
}

/** A one-character severity mark for the card header, for a reader scanning the left edge of the rail
 * rather than reading every line. Empty for "low" - the absence of a mark is itself the signal. */
export function severityGlyph(sev: Severity): string {
  switch (sev) {
    case 'critical': return '‼'; // ‼
    case 'high': return '▲'; // ▲
    case 'normal': return '•'; // •
    case 'low': return '';
  }
}

/** Same-kind events the feed groups within one sim minute, if there are two or more. Never contact
 * events (`contact.new`/`nai.contact` are not in this set at all) - a significant contact is exactly the
 * kind of thing grouping must never bury. `order.cancelled`/`order.rejected` are deliberately excluded
 * too: each is a decision worth reading on its own. */
const GROUPABLE_KINDS = new Set<EventKind>([
  'unit.pinned', 'unit.unpinned', 'unit.broken',
  'order.issued', 'order.tx', 'order.received', 'order.exec', 'order.done',
  'pl.crossed', 'contact.lost',
]);

export interface FeedGroup {
  /** Synthetic, stable for the same (kind, minute, detail) bucket and disjoint from real event ids
   * (positive) and the periodic SITREP's synthetic ids (small negative, `-tick - 1`). */
  id: number;
  kind: EventKind;
  t: number;
  unitIds: number[];
  count: number;
  text: string;
  source: string;
  significant: boolean;
}

/** A feed row is a single templated report, or a rolled-up group of same-kind ones. `'count' in item`
 * tells them apart - a `Report` never has a `count` field. */
export type FeedItem = Report | FeedGroup;

export function isGroup(item: FeedItem): item is FeedGroup {
  return 'count' in item;
}

function groupText(kind: EventKind, count: number, detail: string | undefined): string {
  switch (kind) {
    case 'unit.pinned': return `${count} platoons pinned — heavy fire`;
    case 'unit.unpinned': return `${count} platoons free to maneuver again`;
    case 'unit.broken': return `${count} platoons combat ineffective, breaking contact`;
    case 'order.issued': return `${count} orders issued`;
    case 'order.tx': return `${count} orders transmitting`;
    case 'order.received': return `${count} orders acknowledged`;
    case 'order.exec': return `${count} orders executing`;
    case 'order.done': return `${count} orders complete`;
    case 'pl.crossed': return `${count} platoons crossed ${detail ?? 'a phase line'}`;
    case 'contact.lost': return `${count} contacts lost`;
    default: return `${count} ${kind} events`;
  }
}

/** A stable id for a (kind, minute, detail) bucket, disjoint from every id `triageEvents` might see: real
 * event ids are positive, the periodic SITREP's synthetic ids sit in `[-(a few hundred thousand), -1]`.
 * `-1e9` and below is free. */
function groupId(kind: EventKind, minute: number, detail: string | undefined): number {
  const s = `${kind}:${minute}:${detail ?? ''}`;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return -1_000_000_000 - Math.abs(h);
}

export interface TriageOptions {
  /** Sim ticks per grouping window. Defaults to one sim minute (`TICKS_PER_MIN`); overridable so a test
   * does not have to build ten-tick-wide fixtures to see two events land in different windows. */
  minuteTicks?: number;
}

/**
 * Groups same-kind reports within a sim minute (spec: "6 platoons pinned — heavy fire", "4 orders
 * acknowledged", "3 platoons crossed PL RED"), ranks every row by severity, and returns the feed in
 * display order: newest sim minute first, then most severe first within that minute, then newest first
 * as the final tiebreak. `reports` should already be blue-side only, as `reportText` in `src/ui/reports.ts`
 * produces - triage never looks at `Event.side` because `Report` does not carry it.
 */
export function triageEvents(reports: Report[], opts: TriageOptions = {}): FeedItem[] {
  const minuteTicks = opts.minuteTicks && opts.minuteTicks > 0 ? opts.minuteTicks : TICKS_PER_MIN;
  const buckets = new Map<string, Report[]>();
  const items: FeedItem[] = [];

  for (const r of reports) {
    if (!GROUPABLE_KINDS.has(r.kind)) { items.push(r); continue; }
    const minute = Math.floor(r.t / minuteTicks);
    const key = `${r.kind}:${minute}:${r.kind === 'pl.crossed' ? (r.detail ?? '') : ''}`;
    const list = buckets.get(key);
    if (list) list.push(r); else buckets.set(key, [r]);
  }

  for (const [, list] of buckets) {
    if (list.length < 2) { items.push(list[0]); continue; }
    const minute = Math.floor(list[0].t / minuteTicks);
    const unitIds = [...new Set(list.map((r) => r.unitId).filter((id): id is number => id !== undefined))];
    const t = Math.max(...list.map((r) => r.t));
    items.push({
      id: groupId(list[0].kind, minute, list[0].kind === 'pl.crossed' ? list[0].detail : undefined),
      kind: list[0].kind,
      t,
      unitIds,
      count: list.length,
      text: groupText(list[0].kind, list.length, list[0].detail),
      source: 'S-3',
      significant: list.some((r) => r.significant),
    });
  }

  return items.sort((a, b) => {
    const minuteA = Math.floor(a.t / minuteTicks);
    const minuteB = Math.floor(b.t / minuteTicks);
    if (minuteA !== minuteB) return minuteB - minuteA;
    const sevA = SEVERITY_RANK[severityOf(a)];
    const sevB = SEVERITY_RANK[severityOf(b)];
    if (sevA !== sevB) return sevA - sevB;
    if (a.t !== b.t) return b.t - a.t;
    return b.id - a.id;
  });
}

// ---- Filter chips -------------------------------------------------------------------------------------

export type FeedFilter = 'all' | 'significant' | 'orders';

export const FEED_FILTERS: FeedFilter[] = ['all', 'significant', 'orders'];

const ORDER_KINDS = new Set<EventKind>([
  'order.issued', 'order.tx', 'order.received', 'order.exec', 'order.done', 'order.cancelled', 'order.rejected',
]);

/** SIGNIFICANT shows critical + high severity plus every SITREP (the staff's own periodic report is
 * always worth a look, whatever its severity rank); ORDERS shows only the order lifecycle - a card's
 * whole life from issued to done, cancelled or refused. */
export function matchesFilter(item: FeedItem, filter: FeedFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'orders') return ORDER_KINDS.has(item.kind);
  if (item.kind === 'sitrep') return true;
  const sev = severityOf(item);
  return sev === 'critical' || sev === 'high';
}

// ---- Persisted filter choice ---------------------------------------------------------------------------

const FILTER_LS_KEY = 'wt.feed.filter';

/** Reads the player's last filter choice. Private windows, storage-denied browsers, and plain absence
 * all fall back to ALL rather than throwing. */
export function loadFeedFilter(): FeedFilter {
  try {
    const v = localStorage.getItem(FILTER_LS_KEY);
    if (v === 'all' || v === 'significant' || v === 'orders') return v;
  } catch {
    // Private window or storage denied: default to ALL, same as a first visit.
  }
  return 'all';
}

export function saveFeedFilter(filter: FeedFilter): void {
  try {
    localStorage.setItem(FILTER_LS_KEY, filter);
  } catch {
    // Nothing to persist to; the choice just does not survive a reload.
  }
}
